﻿import {
  AlertCircle,
  ArrowLeft,
  Archive,
  Ban,
  Loader2,
  MoreVertical,
  Pause,
  Play,
  Rocket,
  Ticket,
  FileText,
} from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { EventStatus, UpdateEventRequest } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useCancelEventMutation,
  useEventQuery,
  useEventStatusMutation,
  useUpdateEventMutation,
} from "@/features/events/api";
import {
  EventForm,
  type EditEventFormValues,
} from "@/features/events/event-form";
import {
  formatEventDate,
  isoToLocalInput,
  localInputToIso,
} from "@/features/events/date-helpers";
import { TicketTypesManager } from "@/features/events/ticket-types-manager";

type EventTab = "details" | "tickets";
const VALID_TABS: readonly EventTab[] = ["details", "tickets"] as const;

const STATUS_LABELS: Record<EventStatus, string> = {
  Draft: "Taslak",
  Published: "Yayında",
  Paused: "Duraklatıldı",
  Cancelled: "İptal",
  Ended: "Sona Erdi",
  Archived: "Arşivde",
};

const STATUS_VARIANTS: Record<
  EventStatus,
  "success" | "warning" | "secondary" | "destructive" | "outline" | "default"
> = {
  Draft: "outline",
  Published: "success",
  Paused: "warning",
  Cancelled: "destructive",
  Ended: "secondary",
  Archived: "secondary",
};

export function EventDetailPage() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? "";
  const { data: evt, isLoading, error } = useEventQuery(slug);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: EventTab = VALID_TABS.includes(tabParam as EventTab)
    ? (tabParam as EventTab)
    : "details";

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams.toString());
    if (value === "details") next.delete("tab");
    else next.set("tab", value);
    const query = next.toString();
    navigate(`/events/${slug}${query ? `?${query}` : ""}`, { replace: true });
  };

  if (isLoading) {
    return (
      <DashboardPage>
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardPage>
    );
  }

  if (error || !evt) {
    return (
      <DashboardPage>
        <Card>
          <CardHeader className="items-center text-center">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <CardTitle>Etkinlik bulunamadı</CardTitle>
            <CardDescription>Yanlış link ya da erişim yok olabilir.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button asChild>
              <Link to="/events">Etkinliklere dön</Link>
            </Button>
          </CardContent>
        </Card>
      </DashboardPage>
    );
  }

  const editable = !["Cancelled", "Archived"].includes(evt.status);
  const ticketCount = evt.ticketTypes.length;

  return (
    <DashboardPage>
      {/* Slim breadcrumb — tek satır, minimal */}
      <Link
        to="/events"
        className="mb-2 inline-flex items-center text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Etkinliklere dön
      </Link>

      {/* Kompakt başlık bloğu: başlık + status badge + kebab menü tek satırda */}
      <div className="mb-1 flex items-start gap-2">
        <h1 className="flex-1 text-xl font-semibold leading-tight sm:text-2xl">
          {evt.title}
        </h1>
        <Badge
          variant={STATUS_VARIANTS[evt.status]}
          className="mt-0.5 shrink-0"
        >
          {STATUS_LABELS[evt.status]}
        </Badge>
        <StatusMenu slug={slug} status={evt.status} />
      </div>

      <p className="text-xs text-muted-foreground">
        <span className="font-mono">/events/{evt.slug}</span>
        <span className="mx-1.5">·</span>
        {formatEventDate(evt.startsAt)} — {formatEventDate(evt.endsAt)}
      </p>

      {evt.status === "Cancelled" && evt.cancellationReason && (
        <Card className="mt-4 border-destructive/50 bg-destructive/5">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">İptal Nedeni</CardTitle>
            <CardDescription className="text-xs">{evt.cancellationReason}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
        <TabsList>
          <TabsTrigger value="details">
            <FileText className="mr-1.5 h-4 w-4" />
            Detaylar
          </TabsTrigger>
          <TabsTrigger value="tickets">
            <Ticket className="mr-1.5 h-4 w-4" />
            Biletler
            {ticketCount > 0 ? (
              <span className="ml-1.5 rounded-full bg-muted-foreground/15 px-1.5 py-0.5 text-xs font-normal">
                {ticketCount}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <Card>
            <CardContent className="pt-5">
              {!editable ? (
                <p className="mb-3 text-xs text-muted-foreground">
                  Bu etkinlik artık düzenlenemez.
                </p>
              ) : null}
              {editable ? (
                <EditEventBlock slug={slug} evt={evt} />
              ) : (
                <ReadOnlyMeta evt={evt} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tickets">
          <Card>
            <CardContent className="pt-5">
              <TicketTypesManager
                eventSlug={slug}
                ticketTypes={evt.ticketTypes}
                eventDefaultTaxRate={evt.defaultTaxRate}
                disabled={!editable}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardPage>
  );
}

// ---- Sub components ----

/**
 * Kebab (⋮) menüsüyle durum aksiyonları: Yayınla / Duraklat / Yayına Al / İptal Et / Arşivle.
 * Kompakt mobile-first tasarım — başlık satırına inline oturur. İptal akışı için
 * ayrı inline panel açılır (reason textarea + onay). Arşivleme için native confirm.
 *
 * Dropdown davranışı `locale-switcher.tsx` ile aynı pattern: outside click + Escape.
 */
function StatusMenu({ slug, status }: { slug: string; status: EventStatus }) {
  const mutation = useEventStatusMutation(slug);
  const cancelMutation = useCancelEventMutation(slug);
  const [open, setOpen] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = (action: "publish" | "pause" | "resume" | "archive", successMsg: string) => {
    setOpen(false);
    mutation.mutate(action, {
      onSuccess: () => toast.success(successMsg),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleCancelSubmit = () => {
    cancelMutation.mutate(
      { reason: cancelReason || null },
      {
        onSuccess: () => {
          toast.success("Etkinlik iptal edildi.");
          setShowCancel(false);
          setCancelReason("");
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  // Terminal durumlar: aksiyon yok → menü gösterme
  if (status === "Archived" || status === "Cancelled") {
    return null;
  }

  interface MenuAction {
    label: string;
    icon: typeof Rocket;
    onClick: () => void;
    destructive?: boolean;
  }

  const actions: MenuAction[] = [];
  if (status === "Draft") {
    actions.push({
      label: "Yayınla",
      icon: Rocket,
      onClick: () => run("publish", "Etkinlik yayınlandı."),
    });
  }
  if (status === "Published") {
    actions.push({
      label: "Duraklat",
      icon: Pause,
      onClick: () => run("pause", "Duraklatıldı."),
    });
  }
  if (status === "Paused") {
    actions.push({
      label: "Yayına Al",
      icon: Play,
      onClick: () => run("resume", "Tekrar yayında."),
    });
  }
  if (status === "Published" || status === "Paused") {
    actions.push({
      label: "İptal Et",
      icon: Ban,
      onClick: () => {
        setOpen(false);
        setShowCancel(true);
      },
      destructive: true,
    });
  }
  actions.push({
    label: "Arşivle",
    icon: Archive,
    onClick: () => {
      setOpen(false);
      if (confirm("Arşivlemek kalıcıdır, geri alınamaz. Emin misin?")) {
        run("archive", "Arşivlendi.");
      }
    },
  });

  return (
    <>
      <div ref={rootRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={mutation.isPending}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Etkinlik aksiyonları"
          className={cn(
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
            mutation.isPending && "opacity-60",
          )}
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MoreVertical className="h-4 w-4" />
          )}
        </button>

        {open && (
          <div
            role="menu"
            className="absolute end-0 top-full z-50 mt-1 min-w-[10rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
          >
            {actions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.label}
                  role="menuitem"
                  type="button"
                  onClick={a.onClick}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors",
                    a.destructive
                      ? "text-destructive hover:bg-destructive/10"
                      : "hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-start">{a.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="w-full max-w-md space-y-3 rounded-lg border bg-background p-4 shadow-xl">
            <div>
              <h3 className="text-base font-semibold">Etkinliği İptal Et</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Katılımcılara iletilecek kısa bir açıklama (opsiyonel).
              </p>
            </div>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Örn: Sanatçı sağlık nedeniyle konseri iptal etti."
            />
            <p className="text-xs text-muted-foreground">
              Not: MVP&apos;de iadeler manuel işlenir.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowCancel(false)}>
                Vazgeç
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancelSubmit}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                )}
                İptali Onayla
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EditEventBlock({
  slug,
  evt,
}: {
  slug: string;
  evt: {
    id: string;
    title: string;
    description: string | null;
    coverImageUrl: string | null;
    venueName: string | null;
    venueAddress: string | null;
    venueCity: string | null;
    latitude: number | null;
    longitude: number | null;
    startsAt: string;
    endsAt: string;
    defaultTaxRate: number;
  };
}) {
  const mutation = useUpdateEventMutation(slug);

  const onSubmit = async (values: EditEventFormValues) => {
    const payload: UpdateEventRequest = {
      title: values.title,
      description: values.description || null,
      coverImageUrl: values.coverImageUrl || null,
      venueName: values.venueName || null,
      venueAddress: values.venueAddress || null,
      venueCity: values.venueCity || null,
      latitude: values.latitude ?? null,
      longitude: values.longitude ?? null,
      startsAt: localInputToIso(values.startsAt)!,
      endsAt: localInputToIso(values.endsAt)!,
      defaultTaxRate: values.defaultTaxRate,
    };

    try {
      await mutation.mutateAsync(payload);
      toast.success("Kaydedildi.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kaydedilemedi.");
    }
  };

  return (
    <EventForm
      mode="edit"
      eventId={evt.id}
      isSubmitting={mutation.isPending}
      defaultValues={{
        title: evt.title,
        description: evt.description ?? "",
        coverImageUrl: evt.coverImageUrl ?? "",
        venueName: evt.venueName ?? "",
        venueAddress: evt.venueAddress ?? "",
        venueCity: evt.venueCity ?? "",
        latitude: evt.latitude,
        longitude: evt.longitude,
        startsAt: isoToLocalInput(evt.startsAt),
        endsAt: isoToLocalInput(evt.endsAt),
        defaultTaxRate: evt.defaultTaxRate,
      }}
      onSubmit={onSubmit}
    />
  );
}

function ReadOnlyMeta({ evt }: { evt: { title: string; venueName: string | null; startsAt: string; endsAt: string } }) {
  return (
    <dl className="grid gap-3 text-sm sm:grid-cols-2">
      <Row label="Başlık" value={evt.title} />
      <Row label="Mekan" value={evt.venueName ?? "—"} />
      <Row label="Başlangıç" value={formatEventDate(evt.startsAt)} />
      <Row label="Bitiş" value={formatEventDate(evt.endsAt)} />
    </dl>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
